type LogLevel = "debug" | "info" | "warn" | "error";

type LogContext = {
  component?: string;
  timestamp?: string;
  [key: string]: any;
};

const isDevelopment = import.meta.env.DEV;

/**
 * Enhanced logger with structured logging and log levels
 */
class Logger {
  private shouldLog(level: LogLevel): boolean {
    // Always log errors and warnings
    if (level === "error" || level === "warn") return true;
    // Only log info and debug in development
    return isDevelopment;
  }

  private formatMessage(
    level: LogLevel,
    component: string,
    message: string,
    context?: LogContext
  ): void {
    if (!this.shouldLog(level)) return;

    const timestamp = new Date().toISOString();
    const prefix = `[${component}:${level.toUpperCase()}]`;
    const fullContext = {
      ...context,
      timestamp,
      level,
    };

    const consoleMethod = level === "error" ? console.error :
                         level === "warn" ? console.warn :
                         console.log;

    if (context && Object.keys(context).length > 0) {
      consoleMethod(prefix, message, fullContext);
    } else {
      consoleMethod(prefix, message);
    }
  }

  /**
   * Log debug information (only in development)
   */
  debug(component: string, message: string, context?: LogContext): void {
    this.formatMessage("debug", component, message, context);
  }

  /**
   * Log informational messages (only in development)
   */
  info(component: string, message: string, context?: LogContext): void {
    this.formatMessage("info", component, message, context);
  }

  /**
   * Log warnings (always logged)
   */
  warn(component: string, message: string, context?: LogContext): void {
    this.formatMessage("warn", component, message, context);
  }

  /**
   * Log errors (always logged)
   */
  error(component: string, message: string, context?: LogContext): void {
    this.formatMessage("error", component, message, context);
  }

  /**
   * Create a scoped logger for a specific component
   */
  scope(component: string) {
    return {
      debug: (message: string, context?: LogContext) =>
        this.debug(component, message, context),
      info: (message: string, context?: LogContext) =>
        this.info(component, message, context),
      warn: (message: string, context?: LogContext) =>
        this.warn(component, message, context),
      error: (message: string, context?: LogContext) =>
        this.error(component, message, context),
    };
  }
}

// Export singleton instance
export const log = new Logger();

// Backward compatibility with existing logger
export const logger = (...params: (string | any)[]) => {
  console.log(params);
};
